/**
 * Printserver Module — UI page.
 *
 * 3 taby:
 *   - Tiskárny: lista + add/delete/test akce
 *   - Fronty: aktivní joby v CUPS, cancel
 *   - Discovery: USB + LAN scan, "Přidat" akce
 *
 * Backend: modules/printserver/tools/*.ts (volání přes api.mcp).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  Input,
  Label,
  Modal,
  PageHeader,
  StatCard,
  StatsGrid,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Toggle,
} from '../../components/ui';
import { api } from '../../lib/api';
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  Network,
  Plus,
  Printer,
  Radar,
  RefreshCw,
  Trash2,
  Usb,
  Wifi,
  X,
} from 'lucide-react';
import { themedConfirm } from '../../lib/themed-dialogs';
import { PrintStatsPanel } from './print-stats';

// ── Types ────────────────────────────────────────────────────────────────────

interface PrinterInfo {
  name: string;
  status: 'idle' | 'printing' | 'stopped' | 'unknown';
  type?: 'usb' | 'lan' | 'parallel' | 'other';
  uri?: string;
  is_default?: boolean;
  accepting_jobs?: boolean;
  state_reasons?: string[];
  state_message?: string;
  location?: string;
}

interface JobInfo {
  id: string;
  printer: string;
  user?: string;
  size?: number;
  time?: string;
}

interface DiscoveredDevice {
  uri: string;
  type?: 'usb' | 'lan' | 'parallel' | 'other';
  make_model?: string;
  info?: string;
}

interface CupsErr {
  ok: false;
  error: string;
  detail?: string;
  hint?: string;
}

interface BridgeStats {
  accepted: number;
  rejected_acl: number;
  rejected_size: number;
  rejected_rate: number;
  failed_print: number;
  success: number;
  last_job_at?: string;
  last_client_ip?: string;
}

interface BridgeInfo {
  printer: string;
  port: number;
  allowlist: string[];
  stats: BridgeStats;
}

// ── Component ────────────────────────────────────────────────────────────────

export default function PrintserverPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState('printers');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [preselectedUri, setPreselectedUri] = useState<string>('');

  // ── Queries ────────────────────────────────────────────────────────────────
  const printersQ = useQuery<{ ok: boolean; printers?: PrinterInfo[] } & Partial<CupsErr>>({
    queryKey: ['printserver', 'list'],
    queryFn: () => api.mcp('printserver.printer_list', {}),
    refetchInterval: 15000,
  });

  const jobsQ = useQuery<{ ok: boolean; jobs?: JobInfo[]; count?: number } & Partial<CupsErr>>({
    queryKey: ['printserver', 'jobs'],
    queryFn: () => api.mcp('printserver.job_list', {}),
    refetchInterval: 5000,
    enabled: activeTab === 'jobs',
  });

  const discoveryQ = useQuery<
    { ok: boolean; devices?: DiscoveredDevice[]; total?: number } & Partial<CupsErr>
  >({
    queryKey: ['printserver', 'discover'],
    queryFn: () => api.mcp('printserver.printer_discover', { include_mdns: true }),
    enabled: false, // manual trigger
  });

  const bridgesQ = useQuery<{ ok: boolean; bridges?: BridgeInfo[]; count?: number }>({
    queryKey: ['printserver', 'bridges'],
    queryFn: () => api.mcp('printserver.bridge_status', {}),
    refetchInterval: 10000,
    enabled: activeTab === 'bridges',
  });

  // ── Mutations ──────────────────────────────────────────────────────────────
  const addMut = useMutation({
    mutationFn: (input: {
      name: string;
      uri: string;
      driver?: string;
      raw: boolean;
      location?: string;
      description?: string;
      raw_socket_port?: number;
      raw_socket_allowlist?: string[];
    }) => api.mcp('printserver.printer_add', input),
    onSuccess: (r: any) => {
      if (r?.ok) {
        toast.success(t('printserver.printerAddedToast', { name: r.name }));
        setAddModalOpen(false);
        qc.invalidateQueries({ queryKey: ['printserver'] });
      } else {
        toast.error(r?.hint || r?.stderr || r?.error || t('printserver.addFailed'));
      }
    },
  });

  const deleteMut = useMutation({
    mutationFn: (name: string) => api.mcp('printserver.printer_delete', { name }),
    onSuccess: (r: any, name) => {
      if (r?.ok) {
        toast.success(t('printserver.printerRemovedToast', { name }));
        qc.invalidateQueries({ queryKey: ['printserver'] });
      } else {
        toast.error(r?.hint || r?.stderr || r?.error || t('printserver.deleteFailed'));
      }
    },
  });

  const testPrintMut = useMutation({
    mutationFn: (input: { name: string; raw?: boolean }) =>
      api.mcp('printserver.printer_test_print', input),
    onSuccess: (r: any) => {
      if (r?.ok) toast.success(t('printserver.testJobSentToast', { id: r.job_id ?? '' }));
      else toast.error(r?.stderr || r?.error || t('printserver.testPrintFailed'));
    },
  });

  const cancelMut = useMutation({
    mutationFn: (jobId: string) => api.mcp('printserver.job_cancel', { job_id: jobId }),
    onSuccess: (r: any, jobId) => {
      if (r?.ok) {
        toast.success(t('printserver.jobCancelledToast', { id: jobId }));
        qc.invalidateQueries({ queryKey: ['printserver', 'jobs'] });
      } else toast.error(r?.error || t('printserver.cancelFailed'));
    },
  });

  // ── CUPS not available banner ──────────────────────────────────────────────
  const cupsErr = printersQ.data && printersQ.data.ok === false ? printersQ.data : null;
  const printers = printersQ.data?.printers ?? [];
  const printingCount = printers.filter((p) => p.status === 'printing').length;
  const stoppedCount = printers.filter((p) => p.status === 'stopped').length;

  return (
    <div className='space-y-4'>
      <PageHeader
        title={t('printserver.pageTitle')}
        description={t('printserver.pageDescription')}
        icon={<Printer />}
        actions={
          <Button
            variant='ghost'
            size='sm'
            icon={<RefreshCw size={14} />}
            onClick={() => qc.invalidateQueries({ queryKey: ['printserver'] })}
          >
            {t('printserver.refreshButton')}
          </Button>
        }
      />

      {cupsErr && (
        <Alert variant='warning'>
          <AlertTriangle size={16} />
          <AlertDescription>
            <strong>{t('printserver.cupsUnavailableTitle')}</strong>
            <div className='text-xs mt-1 text-gray-400'>
              {cupsErr.detail || cupsErr.error}
            </div>
            <div className='text-xs mt-2 font-mono bg-gray-950 p-2 rounded'>
              sudo apt install cups cups-client avahi-utils<br />
              sudo usermod -a -G lpadmin antivandal<br />
              sudo systemctl restart acm-api
            </div>
          </AlertDescription>
        </Alert>
      )}

      <StatsGrid>
        <StatCard label={t('printserver.statPrinters')} value={printers.length} icon={<Printer />} />
        <StatCard label={t('printserver.statPrinting')} value={printingCount} icon={<FileText />} />
        <StatCard label={t('printserver.statStopped')} value={stoppedCount} icon={<X />} />
      </StatsGrid>

      <Tabs defaultTab='printers' onTabChange={setActiveTab}>
        <TabList>
          <Tab id='printers'>{t('printserver.tabPrinters')}</Tab>
          <Tab id='jobs'>{t('printserver.tabJobs')}</Tab>
          <Tab id='bridges'>{t('printserver.tabBridges')}</Tab>
          <Tab id='discovery'>{t('printserver.tabDiscovery')}</Tab>
          <Tab id='stats'>{t('printserver.stats.tab')}</Tab>
        </TabList>

        <TabPanel id='printers'>
          <Card>
            <CardContent>
              <div className='flex justify-between items-center mb-3'>
                <h3 className='text-sm font-medium'>{t('printserver.installedPrintersTitle')}</h3>
                <Button
                  variant='primary'
                  size='sm'
                  icon={<Plus size={14} />}
                  onClick={() => {
                    setPreselectedUri('');
                    setAddModalOpen(true);
                  }}
                  disabled={!!cupsErr}
                >
                  {t('printserver.addPrinterTitle')}
                </Button>
              </div>
              {printers.length === 0 && !cupsErr ? (
                <EmptyState
                  icon={<Printer size={32} />}
                  title={t('printserver.noPrintersTitle')}
                  description={t('printserver.noPrintersDescription')}
                />
              ) : (
                <div className='space-y-2'>
                  {printers.map((p) => (
                    <div
                      key={p.name}
                      className='flex items-center justify-between p-3 bg-gray-900 rounded border border-gray-800'
                    >
                      <div className='flex items-center gap-3'>
                        {p.type === 'usb'
                          ? <Usb size={18} className='text-emerald-400' />
                          : <Wifi size={18} className='text-blue-400' />}
                        <div>
                          <div className='flex items-center gap-2'>
                            <span className='font-medium'>{p.name}</span>
                            {p.is_default && <Badge variant='info'>{t('printserver.defaultBadge')}</Badge>}
                            <Badge variant={p.status === 'idle' ? 'success' : p.status === 'printing' ? 'info' : 'warning'}>
                              {p.status}
                            </Badge>
                          </div>
                          <div className='text-xs text-gray-400 mt-0.5'>
                            {p.uri}
                            {p.location ? ` · ${p.location}` : ''}
                          </div>
                          {p.state_reasons && p.state_reasons.length > 0 && (
                            <div className='text-xs text-amber-400 mt-0.5'>
                              {p.state_reasons.join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className='flex gap-2'>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() => testPrintMut.mutate({ name: p.name, raw: false })}
                          loading={testPrintMut.isPending}
                        >
                          {t('printserver.testButton')}
                        </Button>
                        <Button
                          variant='ghost'
                          size='sm'
                          icon={<Trash2 size={12} />}
                          onClick={async () => {
                            if (await themedConfirm(t('printserver.confirmDeletePrinter', { name: p.name }))) {
                              deleteMut.mutate(p.name);
                            }
                          }}
                          loading={deleteMut.isPending}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel id='jobs'>
          <Card>
            <CardContent>
              <div className='flex justify-between items-center mb-3'>
                <h3 className='text-sm font-medium'>
                  {t('printserver.activeQueueTitle', { count: jobsQ.data?.count ?? 0 })}
                </h3>
                <Button
                  variant='ghost'
                  size='sm'
                  icon={<RefreshCw size={14} />}
                  onClick={() => qc.invalidateQueries({ queryKey: ['printserver', 'jobs'] })}
                >
                  {t('printserver.refreshButton')}
                </Button>
              </div>
              {(jobsQ.data?.jobs ?? []).length === 0 ? (
                <EmptyState
                  icon={<CheckCircle2 size={32} />}
                  title={t('printserver.queueEmptyTitle')}
                  description={t('printserver.queueEmptyDescription')}
                />
              ) : (
                <div className='space-y-1'>
                  {(jobsQ.data?.jobs ?? []).map((j) => (
                    <div
                      key={j.id}
                      className='flex items-center justify-between p-2 bg-gray-900 rounded text-sm'
                    >
                      <div>
                        <span className='font-mono text-emerald-400'>{j.id}</span>
                        <span className='text-gray-400 ml-2'>
                          {j.user} · {j.size ? `${Math.round(j.size / 1024)}KB` : ''} · {j.time}
                        </span>
                      </div>
                      <Button
                        variant='ghost'
                        size='sm'
                        icon={<X size={12} />}
                        onClick={() => cancelMut.mutate(j.id)}
                        loading={cancelMut.isPending}
                      >
                        {t('printserver.cancelJobButton')}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel id='bridges'>
          <Card>
            <CardContent>
              <div className='flex justify-between items-center mb-3'>
                <div>
                  <h3 className='text-sm font-medium'>{t('printserver.activeBridgesTitle')}</h3>
                  <div className='text-xs text-gray-400 mt-0.5'>
                    {t('printserver.bridgesDescription')}
                  </div>
                </div>
                <Button
                  variant='ghost'
                  size='sm'
                  icon={<RefreshCw size={14} />}
                  onClick={() => qc.invalidateQueries({ queryKey: ['printserver', 'bridges'] })}
                >
                  {t('printserver.refreshButton')}
                </Button>
              </div>
              {(bridgesQ.data?.bridges ?? []).length === 0 ? (
                <EmptyState
                  icon={<Network size={32} />}
                  title={t('printserver.noBridgesTitle')}
                  description={t('printserver.noBridgesDescription')}
                />
              ) : (
                <div className='space-y-2'>
                  {(bridgesQ.data?.bridges ?? []).map((b) => (
                    <div
                      key={`${b.printer}:${b.port}`}
                      className='p-3 bg-gray-900 rounded border border-gray-800'
                    >
                      <div className='flex items-center justify-between'>
                        <div>
                          <div className='flex items-center gap-2'>
                            <Network size={16} className='text-emerald-400' />
                            <span className='font-mono text-sm'>{b.printer}:{b.port}</span>
                            <Badge variant='success'>{t('printserver.listeningBadge')}</Badge>
                          </div>
                          <div className='text-xs text-gray-400 mt-1'>
                            {t('printserver.allowlistLabel')} {b.allowlist.length > 0
                              ? b.allowlist.join(', ')
                              : <span className='text-red-400'>{t('printserver.allowlistEmpty')}</span>}
                          </div>
                        </div>
                        <div className='text-xs text-right text-gray-400'>
                          <div>
                            {t('printserver.bridgeStatsSuccessFail', {
                              success: b.stats.success,
                              failed: b.stats.failed_print,
                            })}
                          </div>
                          <div>
                            {t('printserver.bridgeStatsDrops', {
                              acl: b.stats.rejected_acl,
                              rate: b.stats.rejected_rate,
                            })}
                          </div>
                          {b.stats.last_job_at && (
                            <div className='text-emerald-400 mt-1'>
                              {t('printserver.bridgeStatsLast', {
                                ip: b.stats.last_client_ip,
                                time: new Date(b.stats.last_job_at).toLocaleTimeString(),
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel id='discovery'>
          <Card>
            <CardContent>
              <div className='flex justify-between items-center mb-3'>
                <h3 className='text-sm font-medium'>{t('printserver.discoveryTitle')}</h3>
                <Button
                  variant='primary'
                  size='sm'
                  icon={<Radar size={14} />}
                  onClick={() => discoveryQ.refetch()}
                  loading={discoveryQ.isFetching}
                  disabled={!!cupsErr}
                >
                  {t('printserver.findPrintersButton')}
                </Button>
              </div>
              {discoveryQ.data?.devices ? (
                discoveryQ.data.devices.length === 0 ? (
                  <EmptyState
                    icon={<Radar size={32} />}
                    title={t('printserver.noPrintersFoundTitle')}
                    description={t('printserver.noPrintersFoundDescription')}
                  />
                ) : (
                  <div className='space-y-2'>
                    {discoveryQ.data.devices.map((d) => (
                      <div
                        key={d.uri}
                        className='flex items-center justify-between p-3 bg-gray-900 rounded border border-gray-800'
                      >
                        <div className='flex items-center gap-3'>
                          {d.type === 'usb'
                            ? <Usb size={18} className='text-emerald-400' />
                            : <Wifi size={18} className='text-blue-400' />}
                          <div>
                            <div className='text-sm font-mono'>{d.uri}</div>
                            {(d.info || d.make_model) && (
                              <div className='text-xs text-gray-400'>
                                {d.info || d.make_model}
                              </div>
                            )}
                          </div>
                        </div>
                        <Button
                          variant='primary'
                          size='sm'
                          icon={<Plus size={12} />}
                          onClick={() => {
                            setPreselectedUri(d.uri);
                            setAddModalOpen(true);
                          }}
                        >
                          {t('printserver.addButton')}
                        </Button>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                <div className='text-sm text-gray-400 py-8 text-center'>
                  {t('printserver.clickFindPrintersHint')}
                </div>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel id='stats'>
          <PrintStatsPanel mode='module' />
        </TabPanel>
      </Tabs>

      {/* Add printer modal */}
      {addModalOpen && (
        <AddPrinterModal
          preselectedUri={preselectedUri}
          onClose={() => setAddModalOpen(false)}
          onSubmit={(input) => addMut.mutate(input)}
          loading={addMut.isPending}
        />
      )}
    </div>
  );
}

// ── Add printer modal ────────────────────────────────────────────────────────

interface AddPrinterModalProps {
  preselectedUri: string;
  onClose: () => void;
  onSubmit: (input: {
    name: string;
    uri: string;
    driver?: string;
    raw: boolean;
    location?: string;
    description?: string;
    raw_socket_port?: number;
    raw_socket_allowlist?: string[];
  }) => void;
  loading: boolean;
}

function AddPrinterModal({ preselectedUri, onClose, onSubmit, loading }: AddPrinterModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [uri, setUri] = useState(preselectedUri);
  const [raw, setRaw] = useState(false);
  const [driver, setDriver] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [bridgeEnabled, setBridgeEnabled] = useState(false);
  const [bridgePort, setBridgePort] = useState('9100');
  const [bridgeAllowlistRaw, setBridgeAllowlistRaw] = useState('10.119.251.0/24');

  const canSubmit = name.match(/^[A-Za-z0-9_-]+$/) && uri && (raw || driver) &&
    (!bridgeEnabled || (parseInt(bridgePort) >= 1024 && parseInt(bridgePort) <= 65535));

  return (
    <Modal open onClose={onClose} title={t('printserver.addPrinterTitle')}>
      <div className='space-y-3'>
        <div>
          <Label>{t('printserver.nameLabel')}</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('printserver.namePlaceholder')}
          />
        </div>
        <div>
          <Label>{t('printserver.deviceUriLabel')}</Label>
          <Input
            value={uri}
            onChange={(e) => setUri(e.target.value)}
            placeholder={t('printserver.deviceUriPlaceholder')}
          />
        </div>
        <div className='flex items-center gap-2'>
          <Toggle value={raw} onSave={setRaw} />
          <Label className='!mb-0'>{t('printserver.rawQueueLabel')}</Label>
        </div>
        {!raw && (
          <div>
            <Label>{t('printserver.driverLabel')}</Label>
            <Input
              value={driver}
              onChange={(e) => setDriver(e.target.value)}
              placeholder='drv:///sample.drv/generic.ppd'
            />
            <div className='text-xs text-gray-400 mt-1'>
              {t('printserver.driverListHintPrefix')} <code>lpinfo -m</code>{' '}
              {t('printserver.driverListHintSuffix')}
            </div>
          </div>
        )}
        <div>
          <Label>{t('printserver.locationLabel')}</Label>
          <Input value={location} onChange={(e) => setLocation(e.target.value)} />
        </div>
        <div>
          <Label>{t('printserver.descriptionLabel')}</Label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        <div className='border-t border-gray-800 pt-3 space-y-2'>
          <div className='flex items-center gap-2'>
            <Toggle value={bridgeEnabled} onSave={setBridgeEnabled} />
            <Label className='!mb-0'>
              {t('printserver.rawSocketBridgeLabel')}
            </Label>
          </div>
          {bridgeEnabled && (
            <div className='space-y-2 ml-6'>
              <div>
                <Label>{t('printserver.portLabel')}</Label>
                <Input
                  type='number'
                  value={bridgePort}
                  onChange={(e) => setBridgePort(e.target.value)}
                  placeholder='9100'
                />
                <div className='text-xs text-gray-400 mt-1'>
                  {t('printserver.portHintClientSends')} socket(<i>this-node-ip</i>,
                  port).write(zpl) {t('printserver.portHintLikeTpLink')}
                </div>
              </div>
              <div>
                <Label>{t('printserver.ipAllowlistLabel')}</Label>
                <Input
                  value={bridgeAllowlistRaw}
                  onChange={(e) => setBridgeAllowlistRaw(e.target.value)}
                  placeholder='10.0.0.0/8, 192.168.1.50'
                />
                <div className='text-xs text-amber-400 mt-1'>
                  {t('printserver.allowlistWarning')}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className='flex justify-end gap-2 pt-3 border-t border-gray-800'>
          <Button variant='secondary' size='sm' onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant='primary'
            size='sm'
            loading={loading}
            disabled={!canSubmit}
            onClick={() =>
              onSubmit({
                name,
                uri,
                raw,
                driver: !raw ? driver : undefined,
                location: location || undefined,
                description: description || undefined,
                raw_socket_port: bridgeEnabled ? parseInt(bridgePort) : undefined,
                raw_socket_allowlist: bridgeEnabled
                  ? bridgeAllowlistRaw.split(',').map((s) => s.trim()).filter(Boolean)
                  : undefined,
              })}
          >
            {t('printserver.addButton')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
